import { ComponentRef, Injectable, Type } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { AppService } from '@app/app.service';
import { enumHtsChatScope } from '@data-layer/enums/common';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { CompanyModel } from '@data-layer/models/companies';
import { CompaniesService } from '@services/companies.service';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { HtsAssistantDrawerComponent } from '../components/hts-assistant-drawer/hts-assistant-drawer.component';
import { HTS_LINE_PANEL_Z_INDEX } from '../const/hts-assistant.const';
import { enumHtsAssistantMode } from '../enums';
import { HtsLineStatusHelper } from '../helpers/hts-line-status.helper';
import { IHtsAssistantLineRef } from '../interfaces';

export interface IHtsLinesReviewConfig {
  /** The Bill of Lading / invoice whose "Finalize" was clicked: the view opens on their first unverified line. */
  focusShipmentId?: number | null;
  focusInvoiceId?: number | null;
  /** Open on this exact line (History hosts, #13385). */
  focusLineId?: number | null;
  isReadonly?: boolean;
  /** The es-mutual line panel (table row + form) the drawer shows behind its eye button (#13384 F7); omitted → no eye. */
  panelComponent?: Type<unknown> | null;
}

/**
 * "Validate HTS codes" view (#13384): the assistant drawer hosting the lines of the entry that still need classification
 * (demo rework 2026-10-16: validated lines are not stepped through). Owns the sidebar instance and follows `entrySummary$`
 * so statuses refresh after every save. Finalize → pick a line → its assistant, nothing else. An insert steps the view on to
 * the next line still needing classification and closes it once there is none (#13529).
 */
@Injectable({ providedIn: 'root' })
export class HtsLinesReviewService {
  private ref: ComponentRef<HtsAssistantDrawerComponent> | null = null;
  /** The entry re-read is in flight: a second click must not open a second drawer. */
  private isOpening: boolean = false;
  /**
   * The lines shown by the open view: those needing classification when it opened, plus any that start needing it later.
   * A line validated inside the view stays (with its "Validated" chip) so the user sees the outcome; an insert steps on
   * to the next line by itself (#13529), the arrows reach the validated ones.
   */
  private reviewLineIds: number[] = [];

  constructor(
    private appService: AppService,
    private entrySummaryStateService: EntrySummaryStateService,
    private companiesService: CompaniesService,
  ) {}

  /**
   * Statuses change without the user touching a line (a background job verifies the company's other drafts, an insert
   * verifies sibling lines), so the entry is re-read from the backend first; the store copy is the fallback.
   */
  public open(config: IHtsLinesReviewConfig): void {
    const current: EntrySummaryModel = this.entrySummaryStateService.entrySummary$.getValue();
    if (this.isOpening || !current || !current.referenceNumber) {
      return;
    }
    this.isOpening = true;
    this.entrySummaryStateService
      .loadEntrySummary(current.referenceNumber)
      .pipe(take(1))
      .subscribe(
        (loaded: EntrySummaryModel) => {
          this.isOpening = false;
          this.entrySummaryStateService.updateCurrentEntrySummary(loaded);
          this.openWith(loaded, config);
        },
        () => {
          this.isOpening = false;
          this.openWith(current, config);
        },
      );
  }

  public close(): void {
    const ref: ComponentRef<HtsAssistantDrawerComponent> | null = this.ref;
    this.ref = null;
    this.reviewLineIds = [];
    if (ref && this.appService.componentSidebarAsWindowRef === ref) {
      this.appService.closeSidebarAsWindow();
      this.appService.isXlg$.next(false);
    }
  }

  private openWith(entry: EntrySummaryModel, config: IHtsLinesReviewConfig): void {
    const allLines: IHtsAssistantLineRef[] = entry ? HtsLineStatusHelper.flattenLines(entry) : [];
    const lines: IHtsAssistantLineRef[] = allLines.filter(HtsLineStatusHelper.isNeedsClassificationRef);
    if (!lines.length) {
      return;
    }
    const focus: IHtsAssistantLineRef =
      lines.find((line: IHtsAssistantLineRef) => line.lineId === config.focusLineId) ||
      HtsLineStatusHelper.pickFocusLine(lines, config.focusShipmentId || null, config.focusInvoiceId || null);

    this.close();
    this.appService.isXlg$.next(true);
    this.ref = this.appService.openSidebarAsWindow(HtsAssistantDrawerComponent, {
      sidebarHeaderText: '',
      isMd: true,
      isFillMobile: true,
      customZIndex: HTS_LINE_PANEL_Z_INDEX,
    });

    const destroyed$: Subject<void> = new Subject<void>();
    this.ref.onDestroy(() => {
      destroyed$.next();
      destroyed$.complete();
    });

    const company: CompanyModel = this.companiesService.getCompany();
    const drawer: HtsAssistantDrawerComponent = this.ref.instance;
    drawer.mode = config.isReadonly ? enumHtsAssistantMode.Readonly : enumHtsAssistantMode.Live;
    drawer.scope = enumHtsChatScope.Importer;
    drawer.companyId = company ? company.id : null;
    drawer.lines = lines;
    drawer.entryLinesCount = allLines.length;
    this.reviewLineIds = lines.map((line: IHtsAssistantLineRef) => line.lineId);
    drawer.lineId = focus.lineId;
    drawer.invoiceId = focus.invoiceId;
    drawer.selectedLineId = focus.lineId;
    drawer.lineDescription = focus.description;
    drawer.currentHts = focus.htsNumber;
    drawer.verificationSource = focus.verificationSource;
    drawer.verificationMatchedText = focus.matchedText;
    drawer.verificationReason = { key: focus.reasonKey, params: focus.reasonParams };
    drawer.panelComponent = config.panelComponent || null;

    drawer.close.pipe(takeUntil(destroyed$)).subscribe(() => this.close());
    drawer.lineSelected.pipe(takeUntil(destroyed$)).subscribe((lineId: number) => this.selectLine(lineId));
    // an insert verified the line (its siblings with the same description too): fresh statuses first, then step on or finish
    // (the inserted line is read here, synchronously: the payload may be null and the user may move on during the re-read)
    drawer.lineUpdated
      .pipe(takeUntil(destroyed$))
      .subscribe(() => this.reloadAfterInsert(entry.referenceNumber, this.ref, drawer.selectedLineId));

    // Statuses, codes and descriptions follow the entry: every save anywhere on the page refreshes the stepper and the panel.
    this.entrySummaryStateService.entrySummary$
      .pipe(
        filter((current: EntrySummaryModel) => !!current),
        takeUntil(destroyed$),
      )
      .subscribe((current: EntrySummaryModel) => this.syncLines(current));
  }

  private selectLine(lineId: number): void {
    if (!this.ref) {
      return;
    }
    const line: IHtsAssistantLineRef = this.ref.instance.lines.find((l: IHtsAssistantLineRef) => l.lineId === lineId);
    if (line) {
      this.ref.instance.selectLine(line);
    }
  }

  private syncLines(entry: EntrySummaryModel): void {
    if (!this.ref) {
      return;
    }
    const allLines: IHtsAssistantLineRef[] = HtsLineStatusHelper.flattenLines(entry);
    const lines: IHtsAssistantLineRef[] = allLines.filter(
      (line: IHtsAssistantLineRef) => this.reviewLineIds.includes(line.lineId) || HtsLineStatusHelper.isNeedsClassificationRef(line),
    );
    if (!lines.length) {
      this.close();
      return;
    }
    this.reviewLineIds = lines.map((line: IHtsAssistantLineRef) => line.lineId);
    this.ref.instance.setLines(lines, allLines.length);
    const isSelectedGone: boolean = !lines.some((line: IHtsAssistantLineRef) => line.lineId === this.ref.instance.selectedLineId);
    if (isSelectedGone) {
      this.ref.instance.selectLine(HtsLineStatusHelper.pickFocusLine(lines, null));
    }
  }

  /**
   * The store refresh is wanted even when the view is gone meanwhile (the page shows the sibling statuses) — as long as the
   * page still shows that entry. A failed read is reported by the API layer; the view then stays on the inserted line.
   */
  private reloadAfterInsert(referenceNumber: string, ref: ComponentRef<HtsAssistantDrawerComponent>, insertedLineId: number): void {
    this.entrySummaryStateService
      .loadEntrySummary(referenceNumber)
      .pipe(take(1))
      .subscribe((entry: EntrySummaryModel) => {
        const current: EntrySummaryModel = this.entrySummaryStateService.entrySummary$.getValue();
        if (!current || current.referenceNumber !== referenceNumber) {
          return; // the user navigated to another entry meanwhile
        }
        // the store emits synchronously: `syncLines` has refreshed the drawer's lines by the time the step is picked
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.stepAfterInsert(ref, insertedLineId);
      });
  }

  /**
   * #13529: the next line still needing classification — after the inserted one first, then from the top (lines skipped
   * with the arrows). None left → the view closes and the invoice box behind it is back in view. The inserted line is never
   * re-selected: that would reset the conversation just inserted.
   */
  private stepAfterInsert(ref: ComponentRef<HtsAssistantDrawerComponent>, insertedLineId: number): void {
    if (this.ref !== ref) {
      return; // closed, or replaced by a view opened from another box, while the entry was re-read
    }
    const drawer: HtsAssistantDrawerComponent = ref.instance;
    if (drawer.selectedLineId !== insertedLineId) {
      return; // the user moved on meanwhile, or an earlier insert's step already did (a second insert before this re-read)
    }
    if (drawer.isPanelDirty || drawer.isPanelEditing) {
      return; // edits started in the line panel meanwhile: stepping or closing would drop them without asking
    }
    const lines: IHtsAssistantLineRef[] = drawer.lines;
    const index: number = drawer.selectedIndex;
    const next: IHtsAssistantLineRef | null =
      lines.slice(index + 1).find(HtsLineStatusHelper.isNeedsClassificationRef) ||
      lines.slice(0, Math.max(index, 0)).find(HtsLineStatusHelper.isNeedsClassificationRef) ||
      null;
    if (next) {
      drawer.selectLine(next);
    } else {
      this.close();
    }
  }
}
